/**
 * 服务方登录页：手机号 + 口令。
 *
 * ══ 与员工控制台登录页的三点差异（都是「外部机构」这个身份决定的）══
 *
 * ① **没有 TOTP 分支**：服务方账号本期未启用二次验证（表结构已预留，
 *    见 core/providerAuth.ts）。界面因此**不宣称**「已开启二次验证」——
 *    那会给人一种「我的账号被双因子保护着」的错误安全感。
 *
 * ② **演示账号直接写在页面上**（本仓库是演示/联调阶段）。演示服务方账号
 *    带着一个固定口令，且它能读到真实派给该主体的成员姓名与电话 ——
 *    这一点在页面上明说，避免有人把它当成生产凭据。
 *
 * ③ **失败一律照实显示服务端原文**：
 *    · 口令错 / 账号不存在 / 账号停用 / 绑定主体准入失效 → 401「账号或口令不正确」
 *      （服务端对四种情形用同一句话，防手机号枚举；界面不替它区分）；
 *    · 连续 5 次失败 → 403「账号因多次登录失败已锁定，请 N 分钟后重试」，
 *      界面额外给出锁定提示条并显示剩余分钟；
 *    · 同一 IP 10 分钟内超过 15 次 → 429「登录尝试过于频繁，请 N 秒后再试」。
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError, providerLogin } from '../lib/api.ts';
import { formatDate, parseLockMinutes } from '../lib/format.ts';

interface LockInfo {
  minutes: number | null;
}

export function LoginPage({ onLoggedIn }: { onLoggedIn: (token: string) => Promise<void> }) {
  // 预填演示手机号：本仓库当前是演示/联调阶段，验收需要能一键登录。
  const [phone, setPhone] = useState('13900000001');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [locked, setLocked] = useState<LockInfo | null>(null);
  const [throttled, setThrottled] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    setLocked(null);
    setThrottled(false);
    try {
      const result = await providerLogin({ phone: phone.trim(), password });
      await onLoggedIn(result.token);
    } catch (err) {
      if (err instanceof ApiError) {
        // 403 = 账号锁定（文案里带剩余分钟）；429 = IP 限流
        if (err.status === 403) setLocked({ minutes: parseLockMinutes(err.message) });
        if (err.status === 429) setThrottled(true);
        setError(err.message);
      } else {
        setError('登录请求失败，请检查后端服务是否已启动');
      }
      setBusy(false);
    }
  };

  const ready = /^1[3-9]\d{9}$/.test(phone.trim()) && password.length >= 6;

  return (
    <div className="login-page">
      <div className="login-intro">
        <div className="brand-mark">HEW³</div>
        <div className="li-title">服务方工作台</div>
        <div className="li-desc">
          三螺旋企业家生态健康守护系统 · 外部合作机构工作台。
          <br />
          本工作台仅供已通过 HEW³ 准入审核的合作机构使用。
        </div>
        <div className="note-src">
          <b>身份边界</b>
          <br />
          服务方是平台的<b>外部合作机构</b>（机构排班员 / 检测机构对接人），不是联合体内部员工。
          账号不继承任何内部员工权限，授权来源是「绑定的已准入主体」——
          主体被暂停或清退，账号立即失去接单权限。
        </div>
        <div className="note-src" style={{ marginTop: 10 }}>
          <b>数据边界</b>
          <br />
          默认只看得到<b>履约必需信息</b>（成员姓名、联系电话、服务项目、预约时间、成员备注、状态）。
          健康 / 指标 / 报告 / 财富数据一律不下发，除非成员在「隐私与授权」里<b>单独授权给本机构</b>。
          本工作台不提供按成员检索与导出。
        </div>
        <div className="note-src" style={{ marginTop: 10 }}>
          <b>演示账号</b>
          <br />
          手机号 <span className="mono-num">13900000001</span>（首保疗养基地 · 联系人 王排班），
          口令见 <span className="mono-num">apps/api/src/db/providerSeed.ts</span>。
          该账号仅在 <span className="mono-num">SEED_DEMO_DATA</span> 门控下创建，<b>生产环境绝不写入</b>。
        </div>
      </div>

      <form className="login-card" onSubmit={(e) => void submit(e)}>
        <div className="lc-title">机构登录</div>
        <div className="lc-sub">PROVIDER SIGN IN · 仅供已通过准入审核的合作机构使用</div>
        <div className="sp16" />

        {locked && (
          <div className="alert-chip danger">
            <span>!</span>
            <span>
              账号已锁定
              {locked.minutes !== null ? `，剩余 ${locked.minutes} 分钟` : ''}。
              {locked.minutes !== null ? '锁定期间即使口令正确也无法登录。' : ''}
            </span>
          </div>
        )}

        {throttled && (
          <div className="alert-chip warn">
            <span>!</span>
            <span>同一出口 IP 的登录尝试过于频繁（服务端按 IP 限流）。请按提示的秒数等待后再试。</span>
          </div>
        )}

        <div className="field">
          <label className="field-label" htmlFor="provider-phone">
            账号（手机号）
          </label>
          <input
            id="provider-phone"
            className="field-input"
            autoComplete="username"
            inputMode="numeric"
            placeholder="请输入 11 位手机号"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="provider-password">
            口令
          </label>
          <input
            id="provider-password"
            className="field-input"
            type="password"
            autoComplete="current-password"
            placeholder="请输入登录口令"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {error && <div className="field-error">{error}</div>}

        <div className="sp12" />
        <button type="submit" className="btn primary block" disabled={busy || !ready}>
          {busy ? '正在验证…' : '登录'}
        </button>

        <div className="field-hint">
          连续 5 次口令失败将锁定账号 15 分钟（锁定落库，重启服务也不能绕过）；
          同一 IP 每 10 分钟最多 15 次登录尝试。
        </div>
        <div className="field-hint">退出登录即由服务端吊销本次会话，不是只清本地状态。</div>
        <div className="field-hint muted">当前演示日期口径：{formatDate(new Date().toISOString())}</div>
      </form>
    </div>
  );
}
